import DateDialog from '@Components/General/DateDialog';
import DialogTextField from '@Components/General/dialog-content/text-field';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import { DevTool } from '@hookform/devtools';
import useDialog from '@hooks/useDialog';
import useHeader from '@hooks/useHeader';
import {
    DashboardCustomizeOutlined,
    SaveRounded,
    ScheduleSendRounded,
    SendRounded,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Stack,
    Typography,
    Icon,
    FormHelperText,
} from '@mui/material';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
    emailDraftQueryOptions,
    emailRecipientsContactsQueryOptions,
} from '@utils/queryOptions';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import RhfRtlProvider from '@dev/rhf-rtl-provider';
import SplitButton, {
    ButtonOptionProps,
} from '@Components/General/SplitButton';
import {
    getNewFormArrayItems,
    getRemovedFormArrayItems,
} from '@utils/tataUtils';
import useEmailDraftOptions from '../useEmailDraftOptions';
import DraftForm from './form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import useFormMui from '@hooks/useFormMui';
import NoDataComponent from '@Components/General/Feedback/no-data-component';
import EmailContent from '@Components/Specific/EmailContent';
import { enqueueSnackbar } from 'notistack';
import { closeOptions } from '@utils/utils';

// TODO: Add email input validation

const TemplateCard = ({ draft, error, handleSave }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <Box>
            <FormHelperText error>{error?.message}</FormHelperText>
            <EmailContent
                htmlContent={draft?.body}
                error={error}
                overlayComponent={
                    <Button
                        startIcon={<Icon>edit_square</Icon>}
                        onClick={async () => {
                            await handleSave();
                            navigate({ to: 'edit' });
                        }}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            m: 2,
                        }}
                    >
                        {t('Edit Content')}
                    </Button>
                }
                placeholder={
                    <NoDataComponent
                        primary={t('prompts:noData.emailDraftContent.primary')}
                        secondary={t(
                            'prompts:noData.emailDraftContent.secondary',
                        )}
                        Icon={DashboardCustomizeOutlined}
                        action={
                            <Button
                                startIcon={<Icon>edit_square</Icon>}
                                onClick={async () => {
                                    await handleSave();
                                    navigate({ to: 'edit' });
                                }}
                            >
                                {t('emailDraft.Create')}
                            </Button>
                        }
                    />
                }
            />
        </Box>
    );
};

const EditEmailDraft = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { draftId } = useParams({});
    const isNew = draftId === 'new';
    const dialog = useDialog();
    const [scheduleDateOpen, setScheduleDateOpen] = useState(false);

    const { data: draft } = useSuspenseQuery(emailDraftQueryOptions(draftId));

    const createEmailMutation = useMutation({
        mutationKey: ['createEmailDraft'],
        onSuccess: res => navigate({ to: `../${res.id}` }),
    });

    const updateEmailMutation = useMutation({
        mutationKey: ['updateEmailDraft'],
    });

    const saveMutation = isNew ? createEmailMutation : updateEmailMutation;

    const scheduleFormHook = useForm();

    const testForm = useFormMui();

    const saveSchema = z.object({
        subject: z.string().min(1, { message: t('Required') }),
    });

    const sendTestSchema = saveSchema.extend({
        from_email: z.string().email({ message: t('Required') }),
    });

    const sendSchema = sendTestSchema.extend({
        recipients: z.array(z.object({})).min(1, { message: t('Required') }),
    });

    const formHook = useFormMui({
        defaultValues: {
            subject: draft?.subject || '',
            from_email: draft?.from_email || '',
            preview_text: draft?.preview_text || '',
            description: draft?.description || '',
            recipients: draft?.recipients || [],
        },
        errors:
            saveMutation.error?.status === 400 &&
            saveMutation.error?.response?.data,
        resolver: zodResolver(saveSchema),
    });

    const {
        control,
        getValues,
        getFieldState,
        formState: { isDirty, errors },
        setError,
        reset,
    } = formHook;

    const handleSave = async () => {
        return saveMutation.mutateAsync(getSaveArguments()).then(res => {
            reset({
                subject: res?.subject || '',
                from_email: res?.from_email || '',
                preview_text: res?.preview_text || '',
                description: res?.description || '',
                recipients: res?.recipients || [],
            });
            return res;
        });
    };

    const preSend = async (testEmail = false) => {
        let savedData = getValues();
        if (isDirty) {
            await handleSave()
                .then(res => {
                    savedData = res;
                })
                .catch(err => {
                    dialog.close();
                    throw err;
                });
        }
        try {
            if (testEmail) {
                sendTestSchema.parse(savedData);
            } else {
                sendSchema.parse(savedData);
            }
        } catch (error) {
            if (error instanceof z.ZodError) {
                if (!draft.template) {
                    setError('template', {
                        type: 'required',
                        message: t('Content cannot be empty'),
                    });
                }
                error.errors.forEach(err => {
                    setError(err.path[0], {
                        type: err.code,
                        message: err.message,
                    });
                });
            }
            dialog.close();
            throw error;
        }
    };

    useHeader({
        title: t('Email Draft'),
    });

    const getModifiedData = () => {
        let data = getValues();
        // remove non-dirty fields
        Object.keys(data).forEach(key => {
            if (!getFieldState(key).isDirty) delete data[key];
        });

        if (data.recipients) {
            const newRecipients = getNewFormArrayItems(data.recipients);
            const removedRecipients = getRemovedFormArrayItems(
                data.recipients,
                draft.recipients,
            );
            data.recipients = [...newRecipients, ...removedRecipients];
        }
        return data || {};
    };

    const getSaveArguments = () => {
        const data = getModifiedData();
        if (isNew) {
            if (!data.subject) {
                data.subject = t('Untitled Email');
            }
            return data;
        }
        return [draftId, data];
    };

    const scheduleEmailMutation = useMutation({
        mutationKey: ['scheduleEmailDraft'],
        onSuccess: () => {
            dialog.close();
            navigate({ to: '../' });
        },
    });

    const sendEmailMutation = useMutation({
        mutationKey: ['sendEmailDraft'],
        onSuccess: () => {
            dialog.close();
            navigate({ to: '../' });
        },
    });

    const {
        data: { count: contactRecipientsCount },
        refetch: refetchContactRecipients,
    } = useSuspenseQuery(emailRecipientsContactsQueryOptions(draftId));

    const { duplicate, deleteEmail } = useEmailDraftOptions()(draft);

    const testEmailMutation = useMutation({
        mutationKey: ['testEmail'],
        onSuccess: () => {
            dialog.close();
            closeOptions();
            enqueueSnackbar(t('Test email sent successfully'), {
                variant: 'success',
            });
        },
    });

    const splitButtonOptions: ButtonOptionProps[] = [
        {
            label: t('Send'),
            startIcon: <SendRounded />,
            selected: true,
            onClick: async () => {
                await preSend();
                await refetchContactRecipients();

                dialog.open({
                    title: t('Send Email'),
                    message: t('prompts:content.sendEmail', {
                        count: contactRecipientsCount,
                    }),
                    confirmLabel: t('Send'),
                    onConfirm: () => draftId,
                    confirmButtonProps: { variant: 'filled' },
                    confirmMutation: sendEmailMutation,
                    mutationKey: ['sendEmailDraft'],
                });
            },
        },
        {
            label: t('Send Test Email'),
            onClick: async () => {
                await preSend(true);
                await handleSave();
                dialog.open({
                    title: t('Send Test Email'),
                    content: (
                        <DialogTextField
                            label={t('Test Email Address')}
                            type="email"
                            control={testForm.control}
                            name="to_email"
                        />
                    ),
                    confirmLabel: t('Send'),
                    confirmMutation: testEmailMutation,
                    onConfirm: () => ({
                        emailId: draftId,
                        ...testForm.getValues(),
                    }),
                    // onConfirm: async () => {
                    //     console.log(testForm.getValues());

                    //     // await preSend();
                    //     // sendEmailMutation.mutateAsync(draftId);
                    // },
                });
            },
        },
    ];

    const quickOpitons: QuickOption[] = [
        {
            ...duplicate,
            label: t('Copy Draft'),
        },
        {
            ...deleteEmail,
            disabled: isNew,
        },
        {
            label: t('Save'),
            Icon: SaveRounded,
            variant: 'outlined',
            // type: 'submit',
            onClick: handleSave,
            // onClick: formHook.handleSubmit(onSubmit),
            quickButton: true,
            disabled: !isDirty,
        },
        {
            label: t('Schedule'),
            Icon: ScheduleSendRounded,
            onClick: async () => {
                await preSend();

                dialog.open({
                    title: t('Schedule Email'),
                    content: (
                        <DialogTextField
                            label={t('Schedule Date')}
                            type="datetime-local"
                            control={scheduleFormHook.control}
                            name="schedule_time"
                            inputProps={{
                                min: new Date()
                                    .toString()
                                    .toLocalInputDateTime(),
                            }}
                        />
                    ),
                    confirmLabel: t('Schedule'),
                    onConfirm: () => {
                        scheduleEmailMutation.mutateAsync([
                            draftId,
                            scheduleFormHook.control._formValues,
                        ]);
                    },
                });
            },
            variant: 'tonal',
            color: 'secondary',
            quickButton: true,
        },
        {
            element: <SplitButton options={splitButtonOptions} />,
            quickButton: true,
        },
    ];

    return (
        <form>
            <Stack spacing={2}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        // color="warning.main"
                    >
                        <Typography variant="titleLarge">
                            {draft?.subject || t('Untitled Email')}
                        </Typography>
                    </Box>
                    <QuickOptions size="large" options={quickOpitons} />
                </Box>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <DraftForm draft={draft} formHook={formHook} />
                </Stack>
                <TemplateCard
                    draft={draft}
                    error={errors.template}
                    handleSave={handleSave}
                />
                {scheduleDateOpen && (
                    <DateDialog
                        open={scheduleDateOpen}
                        onClose={() => setScheduleDateOpen(false)}
                        onSave={date => {
                            scheduleEmail(date);
                            setScheduleDateOpen(false);
                        }}
                        // defualtDate={new Date()}
                        title={t('Schedule Email')}
                        saveLabel={t('Schedule')}
                    />
                )}
                <RhfRtlProvider>
                    <DevTool control={control} />
                </RhfRtlProvider>
            </Stack>
        </form>
    );
};

export default EditEmailDraft;
